import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import PageTemplate from "../../../../components/PageTemplate";
import Spinner from "../../../../components/Spinner";
import ErrorPage from "../../../../components/ErrorPage";
import Modal from "../../../../components/Modal";
import SlidePageTransition from "../../../../components/transitions/SlidePageTransition";
import { useCan } from "../../../../authz/useCan";
import { MutationRequestsHeader } from "./components/MutationRequestsHeader";
import { MutationRequestsContent } from "./components/MutationRequestsContent";
import { useMutationRequests } from "./hooks/useMutationRequests";
import { useMutationEntityLookups } from "./hooks/useMutationEntityLookups";
import type {
  EntityId,
  MutationErrorModal,
  MutationRequestAction,
} from "./types/mutationRequests.types";
import { getApiErrorMessage, getApiErrorStatus } from "./utils/mutationRequests.utils";

const PERMISSION_RESOURCE = "school.mutations";

const ldsAnimationStyles = `
  @keyframes lds-page-enter {
    from {
      opacity: 0;
      transform: translateY(14px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes lds-section-enter {
    from {
      opacity: 0;
      transform: translateY(18px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .lds-page-enter {
    animation: lds-page-enter 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .lds-section-enter {
    animation: lds-section-enter 480ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  @media (prefers-reduced-motion: reduce) {
    .lds-page-enter,
    .lds-section-enter {
      animation: none;
    }
  }
`;

const MutationRequestsAdminPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialRequestId = searchParams.get("mutationId");
  const canCrudMutationRequests = useCan(PERMISSION_RESOURCE, "crud");
  const canManageMutationRequests = useCan(PERMISSION_RESOURCE, "manage");
  const canReadMutationRequests = canCrudMutationRequests || canManageMutationRequests;
  const canProcessMutationRequests = canCrudMutationRequests || canManageMutationRequests;
  const canDeleteMutationRequests = canCrudMutationRequests || canManageMutationRequests;

  const [mutationToDelete, setMutationToDelete] = useState<EntityId | null>(null);
  const [errorModal, setErrorModal] = useState<MutationErrorModal | null>(null);

  const {
    requests,
    loading,
    refreshing,
    error,
    processingRequestId,
    reload,
    performAction,
    removeRequest,
  } = useMutationRequests(canReadMutationRequests);

  const {
    userLookup,
    parentLookup,
    childLookup,
    familyLookup,
    ensureLookup,
  } = useMutationEntityLookups();

  useEffect(() => {
    if (!canReadMutationRequests) return;
    const allRequests = [...requests.pending, ...requests.history];
    const userIds = allRequests.flatMap((request) => [request.requestedBy, request.reviewedBy].filter(Boolean));
    const parentIds = requests.pending.map((request) => request.requestedBy).filter(Boolean);
    const childIds = allRequests.flatMap((request) => request.relatedChildIds);
    const familyIds = allRequests.flatMap((request) => request.relatedFamilyIds);

    void Promise.all([
      ensureLookup("user", userIds),
      ensureLookup("parent", parentIds),
      ensureLookup("child", childIds),
      ensureLookup("family", familyIds),
    ]);
  }, [canReadMutationRequests, ensureLookup, requests.history, requests.pending]);

  const loadError = useMemo(() => {
    if (!error) return null;
    const status = getApiErrorStatus(error);
    if (status === 403) {
      return {
        status,
        message: t("mutations.forbidden", {
          defaultValue: "You do not have access to this page.",
        }),
      };
    }
    return {
      status: status ?? 500,
      message: t("mutations.error_loading", {
        defaultValue: "Unable to load mutation requests.",
      }),
    };
  }, [error, t]);

  const runAction = async (id: EntityId, action: MutationRequestAction) => {
    if (!canProcessMutationRequests) return;
    try {
      await performAction(id, action);
    } catch (actionError) {
      setErrorModal({
        title: t("common.modal.error", { defaultValue: "Error" }),
        message: getApiErrorMessage(
          actionError,
          t("mutations.action_failed", { defaultValue: "Action failed." })
        ),
        type: "error",
        status: getApiErrorStatus(actionError),
      });
      throw actionError;
    }
  };

  const confirmDelete = (id: EntityId) => {
    if (!canDeleteMutationRequests) return;
    setMutationToDelete(id);
  };

  const handleDelete = async () => {
    if (!canDeleteMutationRequests || !mutationToDelete || processingRequestId) return;
    try {
      await removeRequest(mutationToDelete);
      setMutationToDelete(null);
    } catch (deleteError) {
      setErrorModal({
        title: t("common.modal.error", { defaultValue: "Error" }),
        message: getApiErrorMessage(
          deleteError,
          t("mutations.delete_failed", { defaultValue: "Delete failed." })
        ),
        type: "error",
        status: getApiErrorStatus(deleteError),
      });
    }
  };

  if (loading) {
    return (
      <PageTemplate title={null} wrapperClassName="max-w-none">
        <style>{ldsAnimationStyles}</style>
        <div className="lds-page-enter mx-auto max-w-7xl rounded-[32px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_26px_70px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-slate-950/85 sm:p-6">
          <SlidePageTransition className="space-y-5">
            <MutationRequestsHeader
              t={t}
              pendingCount={0}
              refreshing={false}
              onRefresh={() => void reload(true).catch(() => undefined)}
            />
            <div className="lds-section-enter rounded-[24px] border border-slate-200/80 bg-white p-8 shadow-sm dark:border-white/10 dark:bg-white/5">
              <Spinner />
              <p className="text-center text-sm font-semibold text-slate-600 dark:text-slate-300">
                {t("mutations.loading", { defaultValue: "Loading mutation requests..." })}
              </p>
            </div>
          </SlidePageTransition>
        </div>
      </PageTemplate>
    );
  }

  if (!canReadMutationRequests) {
    return (
      <ErrorPage
        status={403}
        message={t("mutations.forbidden", {
          defaultValue: "You do not have access to this page.",
        })}
      />
    );
  }

  if (loadError && !requests.pending.length && !requests.history.length) {
    return (
      <ErrorPage
        message={loadError.message}
        status={loadError.status}
        onRetry={() => void reload(true).catch(() => undefined)}
      />
    );
  }

  return (
    <PageTemplate title={null} wrapperClassName="max-w-none">
      <style>{ldsAnimationStyles}</style>
      <div className="lds-page-enter mx-auto max-w-7xl">
        <div className="rounded-[32px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_26px_70px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-slate-950/85 sm:p-6">
          <SlidePageTransition className="space-y-5">
          <div className="space-y-6">
            <MutationRequestsHeader
              t={t}
              pendingCount={requests.pending.length}
              refreshing={refreshing}
              onRefresh={() => void reload(true).catch(() => undefined)}
            />

            {loadError && (
              <div className="rounded-[20px] border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-800 dark:border-red-500/40 dark:bg-red-900/25 dark:text-red-100">
                {loadError.message}
              </div>
            )}

            <MutationRequestsContent
              pending={requests.pending}
              history={requests.history}
              canProcess={canProcessMutationRequests}
              canDelete={canDeleteMutationRequests}
              processingRequestId={processingRequestId}
              userLookup={userLookup}
              parentLookup={parentLookup}
              childLookup={childLookup}
              familyLookup={familyLookup}
              initialRequestId={initialRequestId}
              onApprove={(id) => runAction(id, "approve")}
              onReject={(id) => runAction(id, "reject")}
              onRestore={(id) => runAction(id, "restore")}
              onDelete={confirmDelete}
            />
          </div>

          <Modal
            show={Boolean(mutationToDelete)}
            title={t("common.modal.are_you_sure", { defaultValue: "Are you sure?" })}
            message={t("common.modal.action_cannot_be_undone", {
              defaultValue: "This action cannot be undone.",
            })}
            type="confirm"
            onConfirm={handleDelete}
            onClose={() => {
              if (!processingRequestId) setMutationToDelete(null);
            }}
            footer={
              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => setMutationToDelete(null)}
                  disabled={Boolean(processingRequestId)}
                  className="mr-2 w-1/2 rounded-lg bg-gray-300 px-4 py-2 text-black hover:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                >
                  {t("common.cancel", { defaultValue: "Cancel" })}
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={Boolean(processingRequestId)}
                  className="w-1/2 rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-red-500 dark:hover:bg-red-600"
                >
                  {processingRequestId
                    ? t("common.deleting", { defaultValue: "Deleting..." })
                    : t("common.confirm", { defaultValue: "Confirm" })}
                </button>
              </div>
            }
          />

          <Modal
            show={Boolean(errorModal)}
            title={errorModal?.title}
            message={errorModal?.message}
            status={errorModal?.status}
            type={errorModal?.type}
            onClose={() => setErrorModal(null)}
          />
          </SlidePageTransition>
        </div>
      </div>
    </PageTemplate>
  );
};

export default MutationRequestsAdminPage;
